import { Card, faDigits } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  api,
  type ForecastAccuracyItemRow,
  type ForecastAccuracySummaryRow,
  type SiteRow,
} from "../../api.js";

interface AccuracyResponse {
  summary: ForecastAccuracySummaryRow;
  items: ForecastAccuracyItemRow[];
}

export function AccuracyTab() {
  const [weeksCount, setWeeksCount] = useState("4");
  const [kitchenId, setKitchenId] = useState("");

  const sites = useQuery({
    queryKey: ["sites"],
    queryFn: () => api<SiteRow[]>("/sites"),
  });
  const kitchens = (sites.data ?? []).filter((s) => s.kind === "kitchen");

  const queryParams = new URLSearchParams();
  if (weeksCount) queryParams.set("weeksCount", weeksCount);
  if (kitchenId) queryParams.set("kitchenId", kitchenId);

  const accuracy = useQuery({
    queryKey: ["forecast", "accuracy", weeksCount, kitchenId],
    queryFn: () => api<AccuracyResponse>(`/forecast/accuracy?${queryParams.toString()}`),
  });

  const data = accuracy.data;
  const summary = data?.summary;
  const items = data?.items ?? [];

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Filters */}
      <div className="row wrap" style={{ gap: "var(--s3)", alignItems: "flex-end" }}>
        <div className="field">
          <label htmlFor="accuracy-weeks">دوره ارزیابی</label>
          <select
            id="accuracy-weeks"
            className="input"
            value={weeksCount}
            onChange={(e) => {
              setWeeksCount(e.target.value);
            }}
          >
            <option value="2">۲ هفته اخیر</option>
            <option value="4">۴ هفته اخیر (۱ ماه)</option>
            <option value="8">۸ هفته اخیر (۲ ماه)</option>
            <option value="12">۱۲ هفته اخیر (۳ ماه)</option>
          </select>
        </div>

        <div className="field">
          <label htmlFor="accuracy-kitchen">آشپزخانه</label>
          <select
            id="accuracy-kitchen"
            className="input"
            value={kitchenId}
            onChange={(e) => {
              setKitchenId(e.target.value);
            }}
          >
            <option value="">همه آشپزخانه‌ها</option>
            {kitchens.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {accuracy.isLoading && <p className="ink2">در حال ارزیابی دقت پیش‌بینی‌ها...</p>}
      {accuracy.error && (
        <p className="hint" role="alert" style={{ color: "var(--coral-ink)" }}>
          خطا در محاسبه دقت: {accuracy.error.message}
        </p>
      )}

      {summary && (
        <>
          {/* Summary Cards */}
          <div
            className="grid"
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: "var(--s3)",
            }}
          >
            <Card>
              <div className="stack" style={{ gap: "var(--s1)" }}>
                <span className="hint">میانگین خطای مطلق (MAE)</span>
                <span className="t-card num b" style={{ fontSize: "1.5rem" }}>
                  {faDigits(summary.meanAbsoluteError)} پرس
                </span>
                <span className="hint ink2">میانگین اختلاف پیش‌بینی با پخت واقعی</span>
              </div>
            </Card>

            <Card>
              <div className="stack" style={{ gap: "var(--s1)" }}>
                <span className="hint">تعداد روزهای ارزیابی‌شده</span>
                <span className="t-card num b" style={{ fontSize: "1.5rem" }}>
                  {faDigits(summary.totalDays)} روز
                </span>
                <span className="hint ink2">روزهای کاری دارای پخت و پیش‌بینی</span>
              </div>
            </Card>

            <Card>
              <div className="stack" style={{ gap: "var(--s1)" }}>
                <span className="hint">دوره ارزیابی</span>
                <span className="t-card num b" style={{ fontSize: "1.2rem" }}>
                  {summary.period}
                </span>
                <span className="hint ink2">{summary.explanation}</span>
              </div>
            </Card>
          </div>

          {/* Daily Accuracy Table */}
          <Card>
            <table className="table" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>تاریخ</th>
                  <th>پرس پیش‌بینی شده</th>
                  <th>پرس واقعی پخته‌شده</th>
                  <th>خطای مطلق</th>
                  <th>درصد اختلاف</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center", color: "var(--muted)" }}>
                      هیچ سابقه روزانه‌ای برای این بازه یافت نشد.
                    </td>
                  </tr>
                ) : (
                  items.map((row) => {
                    const pct =
                      row.actualPortions > 0
                        ? ((row.absoluteError / row.actualPortions) * 100).toFixed(1)
                        : "0";
                    return (
                      <tr key={row.businessDate}>
                        <td>
                          <strong>{faDigits(row.businessDate)}</strong>
                        </td>
                        <td className="num">{faDigits(row.expectedPortions)}</td>
                        <td className="num">{faDigits(row.actualPortions)}</td>
                        <td
                          className="num b"
                          style={{
                            color: row.absoluteError > 50 ? "var(--coral-ink)" : "var(--teal-ink)",
                          }}
                        >
                          {faDigits(row.absoluteError)} پرس
                        </td>
                        <td className="num hint ink2">{faDigits(pct)}%</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}
